import {
  DEFAULT_BROWSER_MEDIA_PROFILE,
  DEFAULT_TV_MEDIA_PROFILE,
  resolveMediaProfile,
  type MediaProfile,
  type PublishKinds,
  type VideoCodec,
} from '@shareview/tele-engine-core';

/**
 * Build-time config (Vite env) with dev defaults. The POC only targets the dev
 * environment, so the defaults are the dev hosts.
 */
export const appConfig = {
  oidcIssuer: import.meta.env.VITE_OIDC_ISSUER || 'https://sso.shareview.dev',
  oidcClientId: import.meta.env.VITE_OIDC_CLIENT_ID || 'tele-poc-web',
  // Same scopes the nurse app requests.
  oidcScope: import.meta.env.VITE_OIDC_SCOPE || 'openid profile offline_access roles shareview',
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL || 'https://api.shareview.dev',
  defaultRoom: import.meta.env.VITE_DEFAULT_ROOM || 'tele-poc-room-1',
};

export type CallMode = 'nurse' | 'guest';
export type ProfileBase = 'browser' | 'tv';

export interface CallOptions {
  room: string;
  mode: CallMode;
  /** Display name, used verbatim; otherwise mode + signed-in user + tab tag. */
  name?: string;
  /** Which engine default the overrides apply to. */
  profileBase: ProfileBase;
  /** Only the values given in the query string; the rest come from the base profile. */
  profileOverrides: Partial<MediaProfile>;
  forceRelay: boolean;
  autojoin: boolean;
  /** Guest mode: run the pre-call test on load. */
  precall: boolean;
  /** Debug path: connect with a directly minted LiveKit token, bypassing SSO and the API. */
  directUrl?: string;
  directToken?: string;
}

/** Capture heights the profile accepts; 16:9 widths. 1080 is clamped to 720 by the engine. */
const RESOLUTIONS: Record<string, { captureWidth: number; captureHeight: number }> = {
  '180': { captureWidth: 320, captureHeight: 180 },
  '360': { captureWidth: 640, captureHeight: 360 },
  '540': { captureWidth: 960, captureHeight: 540 },
  '720': { captureWidth: 1280, captureHeight: 720 },
  '1080': { captureWidth: 1920, captureHeight: 1080 },
};

const CODECS: readonly VideoCodec[] = ['h264', 'vp8'];
const ROOM_RE = /^tele-poc-[a-z0-9-]{1,40}$/;

const truthy = (v: string | null) => v === '1' || v === 'true' || v === 'on';
const falsy = (v: string | null) => v === '0' || v === 'false' || v === 'off';

/** Parse call options from a query string. Unknown or invalid values fall back to defaults. */
export function parseCallOptions(search: string): CallOptions {
  const q = new URLSearchParams(search);
  const room = q.get('room') ?? appConfig.defaultRoom;
  const codec = q.get('codec')?.toLowerCase() as VideoCodec | undefined;
  const res = q.get('res')?.replace(/p$/i, '');
  const simulcast = q.get('simulcast');
  const name = q.get('name')?.trim();

  const profileOverrides: Partial<MediaProfile> = {};
  if (codec && CODECS.includes(codec)) profileOverrides.codecPolicy = codec;
  if (res && RESOLUTIONS[res]) Object.assign(profileOverrides, RESOLUTIONS[res]);
  if (falsy(simulcast)) profileOverrides.simulcast = false;
  else if (truthy(simulcast)) profileOverrides.simulcast = true;

  return {
    room: ROOM_RE.test(room) ? room : appConfig.defaultRoom,
    mode: q.get('mode') === 'guest' ? 'guest' : 'nurse',
    name: name || undefined,
    profileBase: q.get('profile') === 'tv' ? 'tv' : 'browser',
    profileOverrides,
    forceRelay: truthy(q.get('relay')),
    autojoin: truthy(q.get('autojoin')),
    precall: truthy(q.get('precall')),
    directUrl: q.get('lkUrl') ?? undefined,
    directToken: q.get('lkToken') ?? undefined,
  };
}

/** The engine profile for these options: base default plus query overrides, engine rules applied. */
export function profileFor(opts: Pick<CallOptions, 'profileBase' | 'profileOverrides'>): MediaProfile {
  const base = opts.profileBase === 'tv' ? DEFAULT_TV_MEDIA_PROFILE : DEFAULT_BROWSER_MEDIA_PROFILE;
  return resolveMediaProfile(base, opts.profileOverrides);
}

/** Nurses join with the camera on; guests join with only the microphone. */
export function publishFor(mode: CallMode): PublishKinds {
  return mode === 'guest' ? { audio: true, video: false } : { audio: true, video: true };
}

export function displayNameFor(mode: CallMode, base: string): string {
  return `${mode === 'guest' ? 'Guest' : 'Nurse'} ${base} #${tabTag()}`;
}

/** Short per-tab tag so several tabs of the same user are distinguishable. */
export function tabTag(): string {
  const key = 'tele-poc-tab-tag';
  try {
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;
    const tag = Math.random().toString(36).slice(2, 6);
    sessionStorage.setItem(key, tag);
    return tag;
  } catch {
    return Math.random().toString(36).slice(2, 6);
  }
}
